/**
 * Bi-directional sticky behavior for product details on PDP.
 *
 * When the product info is taller than the viewport, the sticky `top` value
 * is adjusted on every scroll frame by the scroll delta. This creates the
 * effect of both columns (images + info) scrolling together:
 *
 * - Scrolling DOWN: `top` decreases until the bottom aligns with the viewport bottom.
 * - Scrolling UP: `top` increases until the top aligns just below the header.
 *
 * When the product info fits within the viewport, it simply sticks at the top.
 */

(() => {
  const DESKTOP_BREAKPOINT = 1000;

  function init() {
    const productDetails = document.querySelector('.product-details.sticky-content--desktop');
    if (!productDetails) return;

    const target = productDetails.classList.contains('full-height--desktop')
      ? productDetails.querySelector(':scope > .group-block') || productDetails
      : productDetails;

    let stickyTop = 0;
    let lastScrollY = window.scrollY;
    let wasInNaturalFlow = true;

    function getHeaderOffset() {
      const headerHeight =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      return document.querySelector('#header-group #header-component[data-sticky-state="active"]') ? headerHeight : 0;
    }

    function update() {
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        target.style.position = '';
        target.style.top = '';
        productDetails.style.position = '';
        productDetails.style.maxHeight = '';
        productDetails.style.height = '';
        return;
      }

      const headerOffset = getHeaderOffset();
      const viewportHeight = window.innerHeight;
      const elementHeight = target.scrollHeight;
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;

      // Ensure parent stretches to full grid row and doesn't interfere
      productDetails.style.maxHeight = 'none';
      productDetails.style.height = '100%';
      productDetails.style.position = 'relative';
      target.style.position = 'sticky';

      if (elementHeight <= viewportHeight - headerOffset) {
        // Content fits — simple sticky at top
        stickyTop = headerOffset;
      } else {
        const topLimit = headerOffset;
        const bottomLimit = viewportHeight - elementHeight;

        const rect = target.getBoundingClientRect();
        // Natural flow: element hasn't engaged sticky yet.
        // Only detect when scrolling down — on scroll up near the top, rect.top
        // can read higher than stickyTop, which would falsely trigger this branch.
        if (delta >= 0 && rect.top > stickyTop + 1) {
          stickyTop = topLimit;
          wasInNaturalFlow = true;
        } else {
          // On the engagement frame (first frame leaving natural flow), skip the delta.
          // Without this, stickyTop drops below the element's natural position and the
          // element falls back to natural flow, causing a visible downward jump.
          if (wasInNaturalFlow) {
            stickyTop = topLimit;
          } else {
            if (stickyTop > topLimit) stickyTop = topLimit;
            stickyTop = Math.max(bottomLimit, Math.min(topLimit, stickyTop - delta));
          }
          wasInNaturalFlow = false;
        }
      }

      target.style.top = `${stickyTop}px`;
      lastScrollY = scrollY;
    }

    // Initialize
    stickyTop = getHeaderOffset();
    productDetails.style.maxHeight = 'none';
    productDetails.style.height = '100%';
    productDetails.style.position = 'relative';
    target.style.position = 'sticky';
    target.style.top = `${stickyTop}px`;

    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);

    // Accordion toggle — recalculate since height changed
    document.addEventListener(
      'toggle',
      (e) => {
        if (productDetails.contains(e.target)) requestAnimationFrame(update);
      },
      true,
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
